import type { VideoTaskCreateRequest } from "../api/videoService";

/**
 * compile-preview 的**重算指纹** —— 只有真会改变 prompt 的东西变了才重新编译。
 *
 * ═══ 由来:编排期间每 2.5 秒白跑一次 ═══════════════════════════════════════════
 * 2026-08-19 走查时从 backend 日志里撞见:编排/出图期间 `frames/presign` 与
 * `compile-preview` **每 1.7 秒交替一次、连打 40 秒**。链路是:
 *
 *   `pollFrameGen` 每 2500ms 轮询
 *     → 每轮 `presignFrames` 重签全部帧,**签名 URL 每次都不一样**(带 sig/exp)
 *     → 新 URL 写进 `form.panelFirstFrameUrls` / `firstFrameUrl`
 *     → `buildTaskRequest` 把这些 URL 放进请求体
 *     → `useCompilePreview` 原来拿 `JSON.stringify(req)` 当依赖键 → **每轮都变**
 *     → compile-preview 每轮重跑(backend 一次 + ai 一次)
 *
 * 一条 30s 的片编排加出图要几分钟,这就是几十到上百次纯白跑 —— 而右栏显示的那条
 * prompt 一个字都不会变。
 *
 * ═══ 为什么是「归一」不是「删掉」═══════════════════════════════════════════════
 * 签名 URL 本身不进 prompt(prompt 从 panelScenes/panelMotions 这些文本字段编),但
 * 首帧**有没有**会决定 I2V / T2V —— 那是真会改 prompt 的。所以把 URL 压成「有/无」:
 *   · 同一张图重新签名 → 指纹不变 → 不重编译 ✓
 *   · 首帧从无到有(或某一镜刚出图)→ 指纹变 → 照旧重编译 ✓
 * 整个剔掉的话第二条就丢了,那比多跑几次更糟(用户刚出的首帧不会反映到预览上)。
 */

/** 这个位置上「有没有东西」—— 空串 / 空白 / undefined 一律算没有。 */
function present(url?: string | null): boolean {
  return !!url && url.trim().length > 0;
}

export function previewFingerprint(req: VideoTaskCreateRequest): string {
  const normalized = {
    ...req,
    // ★这三个是**全部**带签名的字段(与 buildTaskRequest 的返回逐一对过:
    //   firstFrameUrl / lastFrameUrl / panelFirstFrameUrls)。
    //   将来 buildTaskRequest 再加带签名的字段,要同步加到这里 —— 否则那 40 秒白跑会复发。
    firstFrameUrl: present(req.firstFrameUrl),
    lastFrameUrl: present(req.lastFrameUrl),
    panelFirstFrameUrls: req.panelFirstFrameUrls?.map(present),
  };
  return JSON.stringify(normalized);
}
